import React from 'react'
import { addReferralCode, getAllReferralCodes, updateReferralCode, deleteReferralCode, getUserByID, getAllBookings } from '../utils';
import { getDatabase, ref, get } from 'firebase/database';
import { useState, useEffect } from 'react';
import DataTable from '../DataTable';
import { Button, Modal, Form } from 'react-bootstrap';

export default function ReferralCodes() {
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [code, setCode] = useState('');
    const [commissionRate, setCommissionRate] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [editedCode, setEditedCode] = useState('');
    const [editedCommissionRate, setEditedCommissionRate] = useState('');
    const [showBookingsModal, setShowBookingsModal] = useState(false);
    const [selectedReferralCode, setSelectedReferralCode] = useState('');
    const [selectedReferralBookings, setSelectedReferralBookings] = useState([]);


    const formatBookingDate = (date) => {
        if (!date) return '—';
        if (typeof date === 'number') {
            return new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
        const parsed = new Date(date);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
        return date;
    };

    const fetchData = async () => {
        try {
            const [allReferralCodesData, allBookings, usersSnapshot] = await Promise.all([
                getAllReferralCodes(),
                getAllBookings(),
                get(ref(getDatabase(), 'users'))
            ]);

            console.log("allReferralCodesData", allReferralCodesData);

            // Create map of userId -> referralCode
            const usersData = usersSnapshot.exists() ? usersSnapshot.val() : {};
            const userReferralMap = {};
            Object.entries(usersData).forEach(([userId, userData]) => {
                if (userData.refferalCode) {
                    userReferralMap[userId] = userData.refferalCode.toUpperCase();
                }
            });

            // Count bookings per referral code and keep booking details for the view modal
            const bookingCounts = {};
            const bookingsByReferralCode = {};
            (allBookings || []).forEach((booking) => {
                if (booking.owner) {
                    const ownerReferralCode = userReferralMap[booking.owner];
                    if (ownerReferralCode) {
                        bookingCounts[ownerReferralCode] = (bookingCounts[ownerReferralCode] || 0) + 1;
                        if (!bookingsByReferralCode[ownerReferralCode]) {
                            bookingsByReferralCode[ownerReferralCode] = [];
                        }
                        bookingsByReferralCode[ownerReferralCode].push({
                            id: booking.id,
                            name: booking.listingTitle || 'Untitled Booking',
                            date:
                                booking.tripDate ||
                                booking.tripData?.startTimestamp ||
                                booking.createdAt ||
                                null,
                        });
                    }
                }
            });

            Object.values(bookingsByReferralCode).forEach((bookings) => {
                bookings.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    if (Number.isNaN(dateA.getTime()) && Number.isNaN(dateB.getTime())) return 0;
                    if (Number.isNaN(dateA.getTime())) return 1;
                    if (Number.isNaN(dateB.getTime())) return -1;
                    return dateB - dateA;
                });
            });

            // Fetch user names for codes with userId and add booking counts
            const codesWithDetails = await Promise.all(
                allReferralCodesData.map(async (code) => {
                    const codeUpper = code.code?.toUpperCase();
                    const bookingCount = bookingCounts[codeUpper] || 0;
                    const bookings = bookingsByReferralCode[codeUpper] || [];

                    if (code.userId) {
                        try {
                            const userData = await getUserByID(code.userId);
                            return {
                                ...code,
                                userName: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Unknown User',
                                totalBookings: bookingCount,
                                bookings,
                            };
                        } catch (err) {
                            console.error('Error fetching user for code:', code.code, err);
                            return { ...code, userName: 'Unknown User', totalBookings: bookingCount, bookings };
                        }
                    }
                    return { ...code, userName: null, totalBookings: bookingCount, bookings };
                })
            );

            // Sort: Admin-created codes (no userId) first, then by createdAt (newest first)
            codesWithDetails.sort((a, b) => {
                // Admin-created codes (no userId) go to top
                const aIsAdmin = !a.userId;
                const bIsAdmin = !b.userId;

                if (aIsAdmin && !bIsAdmin) return -1;
                if (!aIsAdmin && bIsAdmin) return 1;

                // Within same group, sort by createdAt (newest first)
                const timeA = a.createdAt || 0;
                const timeB = b.createdAt || 0;
                return timeB - timeA;
            });

            setData(codesWithDetails);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const handleEditReferralClick = (row) => {
        setSelectedReferral(row);
        setEditedCode(row.code || '');
        setEditedCommissionRate(
            row.commissionRate === undefined || row.commissionRate === null
                ? ''
                : row.commissionRate
        );
        setShowEditModal(true);
    };
    
    const handleDeleteReferralClick = (row) => {
        setSelectedReferral(row);
        setShowDeleteModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const handleViewBookingsClick = (referralRow) => {
        setSelectedReferralCode(referralRow.code);
        setSelectedReferralBookings(referralRow.bookings || []);
        setShowBookingsModal(true);
    };

    const handleCloseBookingsModal = () => {
        setShowBookingsModal(false);
        setSelectedReferralCode('');
        setSelectedReferralBookings([]);
    };


    useEffect(() => {
        fetchData();
    }, []);


    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!selectedReferral?.id || !editedCode.trim()) {
            return;
        }
        try {
            await updateReferralCode(
                selectedReferral.id,
                editedCode.trim(),
                editedCommissionRate,
                selectedReferral.userId
            );
            fetchData();
            handleCloseEditModal();
        } catch (error) {
            console.error('Error updating referral code:', error);
            window.alert('Could not update this referral code. Please try again.');
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedReferral?.id) {
            return;
        }
        try {
            await deleteReferralCode(selectedReferral.id);
            fetchData();
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting referral code:', error);
            window.alert('Could not delete this referral code. Please try again.');
        }
    };

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const handleAddReferralClick = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };


    const columns = [
        {
          Header: 'ID',
          accessor: 'id',
        },
        {
          Header: 'Code',
          accessor: 'code',
        },
        {
          Header: 'Commission Rate',
          accessor: 'commissionRate',
          Cell: ({ value }) => {
            if (value === undefined || value === null || value === 0) {
              return <span style={{ color: '#999' }}>—</span>;
            }
            return formatter.format(value);
          }
        },
        {
          Header: 'Assigned User',
          accessor: 'userName',
          Cell: ({ value, row }) => {
            if (!row.original.userId) {
              return (
                <span style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  ADMIN
                </span>
              );
            }
            return value || <span style={{ color: '#999' }}>—</span>;
          }
        },
        {
          Header: 'Total Bookings',
          accessor: 'totalBookings',
          Cell: ({ value, row }) => (
            <div className="d-flex align-items-center gap-2">
              <span>{value || 0}</span>
              {(value || 0) > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewBookingsClick(row.original);
                  }}
                >
                  View
                </Button>
              )}
            </div>
          ),
        },
        {
          Header: 'Created At',
          accessor: 'createdAt',
          Cell: ({ value }) => {
            if (!value) return <span style={{ color: '#999' }}>—</span>;
            return new Date(value).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            });
          }
        },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        onClick={() => handleEditReferralClick(row.original)}
                        title="Edit referral code"
                    >
                        Edit
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleDeleteReferralClick(row.original)}
                        title="Delete referral code"
                    >
                        Delete
                    </Button>
                </div>
            ),
        },
    ];

    const handleRowClick = (row) => {
    
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addReferralCode(code, parseFloat(commissionRate));
            handleCloseModal();
            fetchData();

            setCode('');
            setCommissionRate('');
        } catch (error) {
            console.error('Error adding referral code:', error);
        }
    };

    return (
        <>
            <div className="row">
                <div className="col">
                    <div className="page-description d-flex justify-content-between">
                        <h1>All Referral Codes</h1>
                        <Button onClick={handleAddReferralClick}>Add Referral Code</Button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col">
                    <div className="card calendar-container">
                        <div className="card-body payments-data-table">
                            <DataTable columns={columns} data={data}  onRowClick={handleRowClick}/>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Referral Code</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    
                        <Form.Group controlId="referralCode">
                            <Form.Label>Referral Code</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter referral code (e.g., START@5)"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="commissionRate">
                            <Form.Label>Commission Rate (e.g., 0.05 for 5%)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                placeholder="Enter commission rate (0.05 = 5%)"
                                value={commissionRate}
                                onChange={(e) => setCommissionRate(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Enter as decimal: 0.05 = 5%, 0.10 = 10%
                            </Form.Text>
                        </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                    <Button variant="primary" type="submit">
                        Add Referral Code
                    </Button>
                </Modal.Footer>
                </Form>
            </Modal>


            <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Referral Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        {selectedReferral?.userName && (
                            <p className="text-muted mb-3">
                                Assigned to {selectedReferral.userName}
                            </p>
                        )}
                        <Form.Group controlId="editedReferralCode">
                            <Form.Label>Referral Code</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter referral code"
                                value={editedCode}
                                onChange={(e) => setEditedCode(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="editedCommissionRate">
                            <Form.Label>Commission Rate (e.g., 0.05 for 5%)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                placeholder="Enter commission rate"
                                value={editedCommissionRate}
                                onChange={(e) => setEditedCommissionRate(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Enter as decimal: 0.05 = 5%, 0.10 = 10%. Leave blank to keep the current rate.
                            </Form.Text>
                        </Form.Group>
                        <div className='mt-3 d-flex gap-2 justify-content-end'>
                            <Button variant="secondary" onClick={handleCloseEditModal}>
                                Close
                            </Button>
                            <Button variant="primary" type="submit">
                                Update Referral Code
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete referral code{' '}
                    <strong>{selectedReferral?.code}</strong>
                    {selectedReferral?.userName ? ` (assigned to ${selectedReferral.userName})` : ''}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showBookingsModal} onHide={handleCloseBookingsModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Bookings for {selectedReferralCode}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReferralBookings.length === 0 ? (
                        <p className="text-muted mb-0">No bookings found for this referral code.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Booking Name</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedReferralBookings.map((booking) => (
                                        <tr key={booking.id}>
                                            <td>{booking.name}</td>
                                            <td>{formatBookingDate(booking.date)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseBookingsModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
